import React, {
    memo, useCallback, useContext, useState,
} from 'react';
import { withRouter } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { get } from 'lodash';

import PropTypes from 'prop-types';
import logo from '../../../assets/image/logo.svg';
import MobileContext from '../../../contexts/MobileContext';
import EmployerContext from '../../../contexts/EmployerContext';
import Utils from '../../../services/UtilsService';
import MenuItem from './MenuItem';
import Button from '../../material-ui-kit/CustomButtons/Button';
import Backdrop from '../../common/Backdrop';
import BarsIcon from './BarsIcon';

import { useMenuItems } from './MenuUtils';
import useStyles from './MenuStyle';
import { SearchInput } from './SearchInput';
import SettingsContext from '../../../contexts/SettingsContext';
import TextService from '../../../services/TextService';
import { RoutePaths } from '../../../constants/RoutePaths';
import { getRouteWithParams } from '../../../utils/routesUtils';

// Modified by AI on 05/14/2026. Edit #4.
const ApplicationMenu = ({ className = '', isMenuHidden = false }) => {
    const { employerId } = useContext(EmployerContext);
    const { isMobile } = useContext(MobileContext);
    const {
        systemPreferences,
    } = useContext(SettingsContext);

    const { t } = useTranslation();

    const classes = useStyles({ isMobile, isMenuHidden });
    const rootClasses = Utils.getClassName([
        classes.root,
        className,
    ]);

    const logoHref = employerId
        ? `${window.location.origin}${getRouteWithParams(RoutePaths.employerPositions, { employerId })}`
        : window.location.origin;

    const [isOpen, setOpenState] = useState(false);
    const [employerNameSearchValue, setEmployerNameSearchValue] = useState('');
    const [isEmployerFilterApplied, setIsEmployerFilterApplied] = useState(false);
    const appMenuMaxEmployersPerOfficeQuantity = get(systemPreferences, 'appMenuMaxEmployersPerOfficeQuantity', 5);

    const toggleMenu = useCallback(() => setOpenState(!isOpen), [isOpen]);
    const toggleFilterApplying = useCallback((e) => {
        e.stopPropagation();
        setIsEmployerFilterApplied(!isEmployerFilterApplied);
    }, [isEmployerFilterApplied]);

    const { menuItems } = useMenuItems({ isEmployerFilterApplied, employerNameSearchValue }) || {};

    const getSearchConfig = useCallback(({ label }) => {
        switch (true) {
            case label === 'EMPLOYERS': return {
                SearchComponent: <SearchInput
                    value={employerNameSearchValue}
                    onChange={setEmployerNameSearchValue}
                    externalClasses={{ container: classes.employerSearchContainer }}
                />,
                EmptyComponent: <div className={classes.noResultsFound}>{t('NO_RESULTS_FOUND', 'No Results Found')}</div>,
                value: employerNameSearchValue,
                displaySearchToMenuLevel: 1,
                applySearchToMenuLevel: 2,
            };
            default: return {
                component: null,
                value: '',
                onChange: () => {},
            };
        }
    }, [
        employerNameSearchValue,
        setEmployerNameSearchValue,
        classes.employerSearchContainer,
        classes.noResultsFound,
        t,
    ]);

    const getViewMoreConfig = useCallback(({ label }) => {
        switch (true) {
            case label === 'EMPLOYERS': return {
                maxQtyItems: appMenuMaxEmployersPerOfficeQuantity,
                viewMoreVisibilityLevel: 2,
            };
            default: return {
                maxQtyItems: null,
                viewMoreVisibilityLevel: 0,
            };
        }
    }, [employerNameSearchValue, setEmployerNameSearchValue]);

    if (isMenuHidden) {
        return (
            <div className={rootClasses}>
                <div className={classes.activeArea}>
                    <div className={classes.logo}>
                        <img className={classes.image} src={logo}/>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={rootClasses}>
            <div className={classes.activeArea}>
                <div className={classes.buttonContainer} onClick={toggleMenu}>
                    <Button
                        className={classes.menuButton}
                        justIcon
                        color='transparent'
                        round
                        testId='header-menu-switch-btn'
                    >
                        <BarsIcon />
                    </Button>
                </div>
                <div className={classes.logo}>
                    <a href={TextService.sanitize(logoHref)}>
                        <img className={classes.image} src={logo}/>
                    </a>
                </div>
            </div>
            { isOpen && <div className={classes.menuRoot} data-test-id='header-menu-container'>
                {(menuItems).map((menuItem, index) => (
                    <MenuItem
                        searchConfig={getSearchConfig(menuItem)}
                        viewMoreConfig={getViewMoreConfig(menuItem)}
                        key={index}
                        menuItem={menuItem}
                        onClick={() => setOpenState(false)}
                        isEmployerFilterApplied={isEmployerFilterApplied}
                        toggleFilterApplying={toggleFilterApplying}
                        menuLevel={1}
                    />
                ))}
            </div> }
            { isOpen && <Backdrop className={classes.backdrop} onClick={() => setOpenState(false)}/> }
        </div>
    );
};

ApplicationMenu.propTypes = {
    history: PropTypes.object,
    className: PropTypes.string,
    isMenuHidden: PropTypes.bool,
};

export default withRouter(memo(ApplicationMenu));
